/**
 * What a ride costs, what a cancellation costs, and what a ride records as paid.
 *
 * Three sections on one page because all three are what the firm has decided about money —
 * and because the first behaves in the opposite way to the other two, which is worth seeing
 * side by side. A tariff is a new version each time: changing one would reprice rides it has
 * already priced. A fee and a payment method are copied onto the ride the moment they apply,
 * so raising either here cannot reach last month and there is nothing to version.
 *
 * That difference decides the saving too. The tariff section posts its own version from its
 * dialog and is not part of the save bar. The fees and the payment method are the two panels
 * that had one Save each, and the bar drives both: it counts the fields that differ from
 * what was loaded, Discard puts them back, and Save sends only the section that changed —
 * so a failure in one leaves the other saved and says so where that section's error went.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { TariffSection } from './TariffSection';
import {
  settingsApi,
  type FeeSettings,
  type PaymentMethod,
  type PaymentSettings,
} from '../api';
import { refusal } from '../errors';
import { checkForm, named } from '../check';
import {
  Banner,
  EmptyState,
  FieldErrors,
  Measured,
  PageHeader,
  SaveBar,
  Section,
  Select,
  SettingRow,
  type FieldProblem,
} from '../../ui';

const methods: readonly PaymentMethod[] = ['Cash', 'Card', 'Account'];

/** Which sections have just been saved, for the few seconds their banner is up. */
type Flash = 'fees' | 'payment';

export function PricesPage() {
  const { t } = useTranslation();
  // The fees' controls, which `checkForm` reads before they are sent (#377).
  const feeControls = useRef<HTMLDivElement>(null);

  const [fees, setFees] = useState<FeeSettings | null>(null);
  const [feeDraft, setFeeDraft] = useState<FeeSettings | null>(null);
  const [feeProblem, setFeeProblem] = useState<FieldProblem | null>(null);

  const [payment, setPayment] = useState<PaymentSettings | null>(null);
  const [paymentDraft, setPaymentDraft] = useState<PaymentSettings | null>(null);
  const [paymentProblem, setPaymentProblem] = useState<FieldProblem | null>(null);

  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState<readonly Flash[]>([]);

  const loadFees = useCallback(async () => {
    setFeeProblem(null);

    try {
      const next = await settingsApi.fees();

      setFees(next);
      setFeeDraft(next);
    } catch (error) {
      setFeeProblem(refusal(error, t, 'settings.couldNotLoad'));
    }
  }, [t]);

  const loadPayment = useCallback(async () => {
    setPaymentProblem(null);

    try {
      const next = await settingsApi.payment();

      setPayment(next);
      setPaymentDraft(next);
    } catch (error) {
      setPaymentProblem(refusal(error, t, 'settings.couldNotLoad'));
    }
  }, [t]);

  useEffect(() => {
    // eslint-disable-next-line react/set-state-in-effect -- fetching, not cascading
    void loadFees();
    void loadPayment();
  }, [loadFees, loadPayment]);

  useEffect(() => {
    if (flash.length === 0) return undefined;

    const timer = setTimeout(() => setFlash([]), 4000);

    return () => clearTimeout(timer);
  }, [flash]);

  const feeChanges =
    fees === null || feeDraft === null
      ? 0
      : (fees.cancellationFee === feeDraft.cancellationFee ? 0 : 1) +
        (fees.noShowFee === feeDraft.noShowFee ? 0 : 1);

  const paymentChanges =
    payment === null || paymentDraft === null
      ? 0
      : payment.defaultPaymentMethod === paymentDraft.defaultPaymentMethod
        ? 0
        : 1;

  async function save() {
    setSaving(true);

    const done: Flash[] = [];

    if (feeChanges > 0 && feeDraft !== null) {
      setFeeProblem(null);

      const invalid = checkForm(feeControls.current, t);

      if (invalid !== null) {
        setFeeProblem(invalid);
        setSaving(false);
        return;
      }

      try {
        const next = await settingsApi.saveFees(feeDraft);

        setFees(next);
        setFeeDraft(next);
        done.push('fees');
      } catch (error) {
        setFeeProblem(named(refusal(error, t, 'settings.couldNotSave'), feeControls.current, t));
      }
    }

    if (paymentChanges > 0 && paymentDraft !== null) {
      setPaymentProblem(null);

      try {
        const next = await settingsApi.savePayment(paymentDraft);

        setPayment(next);
        setPaymentDraft(next);
        done.push('payment');
      } catch (error) {
        setPaymentProblem(refusal(error, t, 'settings.couldNotSave'));
      }
    }

    setFlash(done);
    setSaving(false);
  }

  function discard() {
    setFeeDraft(fees);
    setPaymentDraft(payment);
    setFeeProblem(null);
  }

  // Editing takes the "Saved" banner down, as the panels' own saved marker did: it said
  // what had been sent, and the moment a box changes it no longer describes the boxes.
  function editFees(next: FeeSettings) {
    setFeeDraft(next);
    setFlash([]);
  }

  function editPayment(next: PaymentSettings) {
    setPaymentDraft(next);
    setFlash([]);
  }

  const changes = feeChanges + paymentChanges;

  // Said out loud rather than left to be inferred from two zeroes. Somebody who has just
  // typed an amount into one box and left the other at zero has made a decision they may
  // not have noticed making.
  const bothOff =
    feeDraft !== null && feeDraft.cancellationFee === 0 && feeDraft.noShowFee === 0;

  return (
    <>
      <PageHeader
        title={t('nav.settings.prices')}
        description={t('nav.settingsAbout.prices')}
      />

      <TariffSection />

      <Section title={t('fees.title')} description={t('fees.intro')}>
        {flash.includes('fees') && (
          <div className="px-5 pt-3">
            <Banner tone="ok">{t('settings.saved')}</Banner>
          </div>
        )}

        {feeProblem !== null && (
          <div className="px-5 pt-3">
            <Banner tone="danger">{feeProblem.message}</Banner>
          </div>
        )}

        {feeDraft === null ? (
          feeProblem === null && <EmptyState>{t('settings.loading')}</EmptyState>
        ) : (
          <FieldErrors root={feeControls} problem={feeProblem} onEdit={() => setFeeProblem(null)}>
            <SettingRow
              label={t('fees.cancellation', { currency: feeDraft.currency })}
              help={t('fees.cancellationHelp')}
            >
              <Measured
                id="cancellationFee"
                unit={feeDraft.currency}
                aria-label={t('fees.cancellation', { currency: feeDraft.currency })}
                type="number"
                min={0}
                max={1000}
                step={0.01}
                value={feeDraft.cancellationFee}
                onChange={(e) =>
                  editFees({ ...feeDraft, cancellationFee: Number(e.target.value) })
                }
              />
            </SettingRow>

            <SettingRow
              label={t('fees.noShow', { currency: feeDraft.currency })}
              help={t('fees.noShowHelp')}
              last
            >
              <Measured
                id="noShowFee"
                unit={feeDraft.currency}
                aria-label={t('fees.noShow', { currency: feeDraft.currency })}
                type="number"
                min={0}
                max={1000}
                step={0.01}
                value={feeDraft.noShowFee}
                onChange={(e) => editFees({ ...feeDraft, noShowFee: Number(e.target.value) })}
              />
            </SettingRow>

            {bothOff && <Note>{t('fees.bothOff')}</Note>}
          </FieldErrors>
        )}
      </Section>

      <Section title={t('payment.title')} description={t('payment.intro')}>
        {flash.includes('payment') && (
          <div className="px-5 pt-3">
            <Banner tone="ok">{t('settings.saved')}</Banner>
          </div>
        )}

        {paymentProblem !== null && (
          <div className="px-5 pt-3">
            <Banner tone="danger">{paymentProblem.message}</Banner>
          </div>
        )}

        {paymentDraft === null ? (
          paymentProblem === null && <EmptyState>{t('settings.loading')}</EmptyState>
        ) : (
          <>
            <SettingRow label={t('payment.fallback')} help={t('payment.fallbackHelp')} last>
              <Select
                id="defaultPaymentMethod"
                aria-label={t('payment.fallback')}
                value={paymentDraft.defaultPaymentMethod ?? ''}
                onChange={(e) =>
                  editPayment({
                    defaultPaymentMethod:
                      e.target.value === '' ? null : (e.target.value as PaymentMethod),
                  })
                }
              >
                {methods.map((method) => (
                  <option key={method} value={method}>
                    {t(`payment.method.${method}`)}
                  </option>
                ))}
                {/* Last, and worded as a decision rather than as an absence. A firm choosing
                    this is saying they would rather admit the gap than guess at it — which is
                    the right answer for one with card readers in every car. */}
                <option value="">{t('payment.method.none')}</option>
              </Select>
            </SettingRow>

            {paymentDraft.defaultPaymentMethod === null && (
              <Note>{t('payment.recordingNothing')}</Note>
            )}

            {/* The question anybody reading this screen is about to ask. */}
            <Note>{t('payment.neverBackwards')}</Note>
          </>
        )}
      </Section>

      <SaveBar
        changes={changes}
        problem={(feeProblem ?? paymentProblem)?.message}
        busy={saving}
        onDiscard={discard}
        onSave={() => void save()}
        discardLabel={t('settings.discard')}
        saveLabel={saving ? t('settings.saving') : t('settings.save')}
      >
        {t('settings.unsaved', { count: changes })}
      </SaveBar>
    </>
  );
}

/**
 * A sentence at the foot of a section that is not a setting: what two zeroes mean, what an
 * unrecorded ride goes on counting as, what saving does not do to last month.
 *
 * It carries the rule above it rather than below, so the last `SettingRow` can close the
 * card cleanly when the note is not there.
 */
function Note({ children }: { children: ReactNode }) {
  return (
    <p className="text-secondary text-ink-3 m-0 px-5 py-3 border-t border-rule leading-[1.4]">
      {children}
    </p>
  );
}
